import { nombreCorto } from "@/lib/nombre-entidad";
import type { CompraAgil } from "@/types";

export type Urgencia = "critica" | "proxima" | "normal";
/** "todas" = sin criterio propio: respeta el orden que ya entrega el backend. */
export type OrdenCompraAgil = "todas" | "cierre" | "reciente" | "monto";

/** Mismo lenguaje visual que estadoMPColors/estadoMPLabels (mock-data.ts), con
 * las variantes propias de Compra Ágil ("cancelada", "proveedor_seleccionado"). */
export const estadoCALabels: Record<string, string> = {
  publicada: "Publicada",
  cerrada: "Cerrada",
  desierta: "Desierta",
  cancelada: "Cancelada",
  proveedor_seleccionado: "Proveedor seleccionado",
};

export const estadoCAColors: Record<string, string> = {
  publicada: "bg-info/15 text-info",
  cerrada: "bg-muted text-muted-foreground",
  desierta: "bg-warning/15 text-warning",
  cancelada: "bg-destructive/15 text-destructive",
  proveedor_seleccionado: "bg-success/15 text-success",
};

/** Días restantes respecto a hoy en hora de Chile (redondeo hacia arriba). */
export function diasRestantesSantiago(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const cierre = new Date(iso);
  const ahora = new Date();
  const ms = cierre.getTime() - ahora.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/** Nivel de urgencia de cierre para una Compra Ágil. */
export function urgenciaCierre(dias: number | null): Urgencia {
  if (dias === null || Number.isNaN(dias)) return "normal";
  if (dias <= 3) return "critica";
  if (dias <= 7) return "proxima";
  return "normal";
}

/** Texto legible para un cierre. */
export function textoCierre(iso: string | null | undefined): {
  fecha: string;
  texto: string;
  urgencia: Urgencia;
} {
  const fecha = iso
    ? new Date(iso).toLocaleDateString("es-CL", { timeZone: "America/Santiago" })
    : "—";
  const dias = diasRestantesSantiago(iso);
  const urgencia = urgenciaCierre(dias);
  if (dias === null) return { fecha, texto: "—", urgencia: "normal" };
  if (dias < 0) return { fecha, texto: "Cerrada", urgencia: "normal" };
  if (dias === 0) return { fecha, texto: "Cierra hoy", urgencia: "critica" };
  if (dias === 1) return { fecha, texto: "En 1 día", urgencia: "critica" };
  return { fecha, texto: `En ${dias} días`, urgencia };
}

/** Detecta ofertas sobre el promedio de la página actual (umbral simple). */
export function esAltaDemanda(ofertas: number | null, promedio: number): boolean {
  if (ofertas == null || Number.isNaN(promedio) || promedio <= 0) return false;
  return ofertas > promedio * 1.25;
}

/** Normaliza el organismo para mostrar el nombre más legible. */
export function organismoDisplay(nombre: string | null | undefined): string {
  return nombreCorto(nombre) || "Organismo no informado";
}

/**
 * Clave de orden para "cierre más próximo": las fechas futuras ordenan
 * ascendente normal (más próxima primero); las ya vencidas o sin fecha se
 * empujan al final (entre ellas, la cerrada más reciente primero) — antes
 * un `fa - fb` ascendente puro hacía que lo vencido hace meses ganara el
 * orden, porque su timestamp es el número más chico de todos.
 */
function claveCierre(item: CompraAgil): number {
  if (!item.fechaCierre) return Infinity;
  const t = new Date(item.fechaCierre).getTime();
  if (Number.isNaN(t)) return Infinity;
  if (t >= Date.now()) return t;
  return Number.MAX_SAFE_INTEGER - t;
}

/** Ordena compras ágiles según el criterio seleccionado. "todas" no
 * reordena — respeta el orden que ya entrega el backend. */
export function ordenarComprasAgiles(
  items: CompraAgil[],
  orden: OrdenCompraAgil,
): CompraAgil[] {
  const sorted = [...items].sort((a, b) => {
    if (orden === "cierre") {
      return claveCierre(a) - claveCierre(b);
    }
    if (orden === "reciente") {
      const fa = a.fechaPublicacion ? new Date(a.fechaPublicacion).getTime() : 0;
      const fb = b.fechaPublicacion ? new Date(b.fechaPublicacion).getTime() : 0;
      return fb - fa;
    }
    if (orden === "monto") {
      return (b.montoDisponibleCLP ?? 0) - (a.montoDisponibleCLP ?? 0);
    }
    return 0;
  });
  return sorted;
}

